import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AlertService } from '../../../../../../core/services/alert/alert.service';
import { take, tap } from 'rxjs';
import { RouteConstants } from '../../../../../../core/constants/routes';
import { ModalComponent } from '../../../../../../shared/components/modal/modal.component';
import { GoApplicationService } from '../go-application/go-application.service';
import { GoApplicationPreparationService } from '../go-application-preparation/go-application-preparation.service';
import { Router } from '@angular/router';
import { GoTerminateSearchDialogMessage } from './constants/go-terminate-search-dialog-message';
import { GoStopAndSaveSearchDialogMessage } from './constants/go-stop-and-save-search-dialog-message';
import { GoCancelSearchDialogMessage } from './constants/go-cancel-search-dialog-message';
import { hasOpenDialogs } from '../../../../../../core/functions/dialog-helper';

@Injectable({
    providedIn: 'root',
})
export class GoManageApplicationService {
    constructor(
        private readonly dialog: MatDialog,
        private readonly alertService: AlertService,
        private readonly goApplicationService: GoApplicationService,
        private readonly goApplicationPreparationService: GoApplicationPreparationService,
        private readonly router: Router,
    ) {}

    terminateSearch(applicationId: string): void {
        if (this.dialog.openDialogs.length > 0) {
            return;
        }

        this.dialog.open(ModalComponent, {
            disableClose: true,
            data: {
                title: GoTerminateSearchDialogMessage.modalTitle,
                content: GoTerminateSearchDialogMessage.modalContent,
                primaryAction: {
                    title: GoTerminateSearchDialogMessage.primaryActionTitle,
                    classList: '!bg-error-700 !text-tertiary-50',
                    action: () => {
                        this.terminateSearchHandler(applicationId);
                    },
                },
            },
        });
    }

    /**
     * Terminates the search process and on success will redirect back to the purpose of filing page.
     * @param applicationId The ID of the application to terminate the search for.
     */
    private terminateSearchHandler(applicationId: string): void {
        if (!applicationId) {
            throw new Error(
                'Application ID is required to terminate the search.',
            );
        }

        this.goApplicationService
            .terminateSearch(applicationId)
            .pipe(
                take(1),
                tap(() => {
                    this.goApplicationPreparationService.resetForm();

                    this.alertService.displaySuccessAlert(
                        GoTerminateSearchDialogMessage.successfulAlertMessage,
                    );

                    this.redirectToPurposeOfFilingRoute();
                }),
            )
            .subscribe();
    }

    cancelSearch(applicationId: string): void {
        if (hasOpenDialogs(this.dialog)) {
            return;
        }

        this.dialog.open(ModalComponent, {
            disableClose: true,
            data: {
                title: GoCancelSearchDialogMessage.modalTitle,
                content: GoCancelSearchDialogMessage.modalContent,
                primaryAction: {
                    title: GoTerminateSearchDialogMessage.primaryActionTitle,
                    classList: '!bg-error-700 !text-tertiary-50',
                    action: () => {
                        this.cancelSearchHandler(applicationId);
                    },
                },
            },
        });
    }

    /**
     * Cancels the search process and on success will redirect back to the purpose of filing page.
     * @param applicationId The ID of the application to cancel the search for.
     */
    private cancelSearchHandler(applicationId: string): void {
        if (!applicationId) {
            throw new Error('Application ID is required to cancel the search.');
        }

        this.goApplicationService
            .cancelSearch(applicationId)
            .pipe(
                take(1),
                tap(() => {
                    this.goApplicationPreparationService.resetForm();

                    this.alertService.displaySuccessAlert(
                        GoTerminateSearchDialogMessage.successfulAlertMessage,
                    );

                    this.redirectToPurposeOfFilingRoute();
                }),
            )
            .subscribe({
                error: () => {
                    this.alertService.displayErrorAlert(
                        GoCancelSearchDialogMessage.errorAlertMessage,
                    );
                },
            });
    }

    stopAndSaveSearch(applicationId: string): void {
        if (this.dialog.openDialogs.length > 0) {
            return;
        }

        this.dialog.open(ModalComponent, {
            disableClose: true,
            data: {
                title: GoStopAndSaveSearchDialogMessage.modalTitle,
                content: GoStopAndSaveSearchDialogMessage.modalContent,
                primaryAction: {
                    title: GoStopAndSaveSearchDialogMessage.primaryActionTitle,
                    action: () => {
                        this.stopAndSaveSearchHandler(applicationId);
                    },
                },
            },
        });
    }

    /**
     * Stops the current search and saves the technical data.
     * @param applicationId The ID of the application to stop and save search for.
     */
    private stopAndSaveSearchHandler(applicationId: string): void {
        if (!applicationId) {
            throw new Error(
                'Application ID is required to stop and save search.',
            );
        }

        this.goApplicationService
            .stopAndSaveSearch(applicationId)
            .pipe(
                take(1),
                tap(() => {
                    this.goApplicationPreparationService.resetForm();

                    this.alertService.displaySuccessAlert(
                        GoStopAndSaveSearchDialogMessage.successfulAlertMessage,
                    );

                    this.redirectToPurposeOfFilingRoute();
                }),
            )
            .subscribe();
    }

    private redirectToPurposeOfFilingRoute(): void {
        this.router.navigate([
            RouteConstants.Go.getFullRoute('purposeOfFiling'),
        ]);
    }
}
